import 'primeflex/primeflex.css';

import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import { toaster } from 'components/v2/toast';
import { ProcessStatus, RoleName } from 'constants/constants';
import { Role } from 'constants/constants';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import React, { useRef, useState } from 'react';
import AuthService from 'services/AuthService';
import CollectionService from 'services/CollectionService';
import CollectionServiceV2 from 'services/CollectionServiceV2';
import { CollectionDashboardModel, EPDCollectionStatus } from 'services/EpdClient';
import MembershipService from 'services/MembershipService';
import styled from 'styled-components';
import useCurrentUser from 'hooks/useCurrentUser';

type TProps = {
  collection: CollectionDashboardModel;
  fetchCollections: () => {};
};

const CollectionTitleRow = ({ collection, fetchCollections }: TProps) => {
  const menu = useRef<Menu>(null);
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const { companyId } = React.useContext(CompanyContext);
  const currentUser = useCurrentUser();
  const hasAccessToCollectionActions =
    currentUser.memberships?.some((x) => x.companyId === companyId && x.roleName !== RoleName.EPD_VERIFIER) ||
    AuthService.isAdmin();

  const acceptCollectionDelete = async () => {
    try {
      await CollectionService.deleteCollection(collection.id!);
      toaster({
        severity: 'success',
        summary: 'Confirmed',
        details: 'Collection has been deleted',
      });
      fetchCollections();
    } catch (error) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: 'Collection deletion error',
      });
      console.error(error);
    }
  };

  const handlePublish = async (id: string) => {
    try {
      const isOwner =
        (
          await MembershipService.getMembershipsByUserAndCompanyAndRole(
            companyId || '',
            Role.EPDOwner,
            AuthService.getUser()?.id!
          )
        ).length > 0;

      if (isOwner || AuthService.isAdmin()) {
        setStatus(ProcessStatus.Fetching);
        await CollectionServiceV2.publishCollection(id);
        toaster({
          severity: 'success',
          summary: 'The collection has been published!',
          details: 'Now it is available on EPD website in EPD library.',
        });
        fetchCollections();
        setStatus(ProcessStatus.Success);
      } else {
        toaster({
          severity: 'error',
          summary: 'Collection has not been published!',
          details:
            'Unfortunately, you are not authorized to publish this collection. Please contact the EPD Owner who is authorized to publish this.',
        });
      }
    } catch (error) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: JSON.stringify(error),
      });
      setStatus(ProcessStatus.Error);
    }
  };

  const handleDepublish = async (id: string) => {
    try {
      const isOwner =
        (
          await MembershipService.getMembershipsByUserAndCompanyAndRole(
            companyId || '',
            Role.EPDOwner,
            AuthService.getUser()?.id!
          )
        ).length > 0;
      if (isOwner || AuthService.isAdmin()) {
        setStatus(ProcessStatus.Fetching);
        await CollectionServiceV2.depublishCollection(id);
        toaster({
          severity: 'success',
          summary: 'Success',
          details: 'Collection has been depublished',
        });
        fetchCollections();
        setStatus(ProcessStatus.Success);
      } else {
        toaster({
          severity: 'error',
          summary: 'Collection has not been depublished!',
          details:
            'Unfortunately, you are not authorized to depublish this collection. Please contact the EPD Owner who is authorized to depublish this.',
        });
      }
    } catch (error) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: JSON.stringify(error),
      });

      setStatus(ProcessStatus.Error);
    }
  };

  const items: MenuItem[] = [
    {
      label: 'Publish collection',
      command: () => {
        handlePublish(collection.id!);
      },
      visible: collection.status !== EPDCollectionStatus.Published && collection.hasPublishedEPDs,
    },
    {
      label: 'Depublish collection',
      command: () => {
        handleDepublish(collection.id!);
      },
      visible: collection.status === EPDCollectionStatus.Published,
    },
    {
      label: 'Delete collection',
      command: () => {
        confirmDelete({
          caption: 'Are you sure you want to delete the collection?',
          description: 'The collection will be deleted and we will not be able to restore it.',
          onConfirm: () => {
            acceptCollectionDelete();
          },
        });
      },
    },
    {
      label: 'Environdec view',
      url: 'https://www.environdec.com/library/collection/' + collection.friendlyUrl,
      target: '_blank',
      visible: collection.status === EPDCollectionStatus.Published,
    },
  ];

  return (
    <CollectionTitle>
      <CollectionName>{collection.name}</CollectionName>
      {hasAccessToCollectionActions && (
        <div>
          <Menu model={items} popup ref={menu} />
          <Button className="border-none surface-section text-gray-900" onClick={(e) => menu.current!.toggle(e)}>
            <i className="pi pi-ellipsis-v font-bold text-lg" style={{ color: '#151515' }} />
          </Button>
        </div>
      )}
    </CollectionTitle>
  );
};

const CollectionTitle = styled.div`
  padding-left: 1.5rem;
  padding-right: 0.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`;

const CollectionName = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  max-height: 4rem;
  overflow: hidden;
  word-break: break-all;
  flex-wrap: wrap;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
`;
export default CollectionTitleRow;
